import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { z } from 'zod';
import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import { i18n } from '~/utils';

import { queryKeysFtpPoller } from './queryKeys';
import { type FtpPoller } from './types';
import { zodFtpPollerPayloadSchema } from './useMutationCreateFtpPoller';

const zodUpdateFtpPollerEndpointSchema = z.tuple([
  z.literal('vadmin'),
  z.literal('ftp_poller'),
  z.literal('secret'),
  z.uuid(),
]);

const zodUpdateFtpPollerPayloadSchema = zodFtpPollerPayloadSchema
  .partial()
  .transform((value) => withoutObjectKeysWhereValueIs(value, [null, undefined]))
  .transform(({ buyerId, ...rest }) => ({
    buyerId: buyerId || null, // Replace empty string with null
    ...rest,
  }));

type UpdateFtpPollerPayload = z.infer<typeof zodUpdateFtpPollerPayloadSchema>;

type UpdateFtpPollerVariables = {
  ftpPollerId: UUID;
  ftpPollerPayload: Partial<UpdateFtpPollerPayload>;
};

type UpdateFtpPollerResponse = FtpPoller;

/**
 * Update a FTP poller secret with a given ID.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-FTP-Secrets/operation/update_ftp_secret_by_id_vadmin_ftp_poller_secret__ftp_id__patch
 */
export const updateFtpPoller = async (
  ftpPollerId: UUID,
  updateFtpPollerPayload: UpdateFtpPollerPayload,
) => {
  try {
    const response = await vestigasApi
      .patch(ENDPOINT.FTP_POLLER.UPDATE(ftpPollerId), {
        json: updateFtpPollerPayload,
        validate: {
          body: zodUpdateFtpPollerPayloadSchema,
          pathSegments: zodUpdateFtpPollerEndpointSchema,
        },
      })
      .json<UpdateFtpPollerResponse>();

    return response;
  } catch (error) {
    // 400 (connection test errors), 412 (large batch), 403 (extreme batch)
    // are handled by the form layer — do not show a toast here for those.
    if (
      error instanceof HTTPError &&
      [400, 403, 412].includes(error.response.status)
    ) {
      throw error;
    }

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(($) => $.mutation.updateFtpPoller.error422, {
            ns: 'ftpPoller',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.mutation.updateFtpPoller.error.generic,
            {
              ns: 'ftpPoller',
            },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * A custom hook to update a FTP poller secret with a given ID.
 * Uses the `useMutation` hook from `@tanstack/react-query` internally.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-FTP-Secrets/operation/update_ftp_secret_by_id_vadmin_ftp_poller_secret__ftp_id__patch
 */
export const useMutationUpdateFtpPoller = (
  options?: UseMutationOptions<
    UpdateFtpPollerResponse,
    unknown,
    UpdateFtpPollerVariables
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateFtpPollerResponse,
    unknown,
    UpdateFtpPollerVariables
  >({
    mutationFn: async ({
      ftpPollerId,
      ftpPollerPayload,
    }: UpdateFtpPollerVariables) =>
      updateFtpPoller(ftpPollerId, ftpPollerPayload),
    onError(_error) {
      // prevent error from bubbling up further unless overridden via mutation options
    },
    onSettled(_data, _error, { ftpPollerId }) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysFtpPoller.getAll(undefined),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysFtpPoller.get(ftpPollerId),
      });
    },
    onSuccess() {
      ToastService.info(
        i18n.t(($) => $.mutation.updateFtpPoller.success, { ns: 'ftpPoller' }),
      );
    },
    ...options,
  });
};
